/**
 * A catalog hit's id, as a link to the store page when the catalog has one.
 *
 * The id is the thing that gets typed into an order, so it is also the thing a
 * chemist wants to open: `corteva` resolves into SIMMATE's CortevaCoRe record,
 * `enamine` into the Enamine store. A catalog with no known resolver renders as
 * plain text rather than a guessed url — see `compoundUrl`. `chemspace` ids resolve
 * nowhere, so a chemspace hit shows its SMILES with store searches for it instead.
 *
 * Shared by the per-run drawer (`TargetTable`) and the board drawer
 * (`BoardTable`), which render the same record; a second copy would drift the
 * first time a catalog was added. Styled like `EvidenceLinks`: brand colour plus
 * the external-link glyph, so an off-site jump looks like the other off-site jumps.
 */
import { ExternalLink } from 'lucide-react'
import type { CatalogHit } from '../api/types'
import { compoundUrl, smilesSearchUrls } from '../lib/utils'

const LINK =
  'inline-flex items-baseline gap-1 rounded font-mono text-brand-300 hover:text-brand-200 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500'

/**
 * `smilesColumn`: the caller shows the SMILES in a column of its own, so it is not
 * repeated here — a chemspace hit then renders only its Enamine search.
 */
export function CompoundId({ hit, smilesColumn = false }: { hit: CatalogHit; smilesColumn?: boolean }) {
  if (hit.source?.toLowerCase() === 'chemspace' && hit.smiles) {
    return (
      <span className="inline-flex flex-wrap items-baseline gap-x-2">
        {!smilesColumn && (
          <span className="break-all font-mono text-slate-200">{hit.smiles}</span>
        )}
        {smilesSearchUrls(hit.smiles).map((s) => (
          <a
            key={s.label}
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            title={`Search ${s.label} for this structure`}
            className={LINK}
          >
            {s.label}
            <ExternalLink aria-hidden="true" className="h-2.5 w-2.5 shrink-0" />
          </a>
        ))}
      </span>
    )
  }
  const label = hit.catalog_id ?? hit.compound_id
  const url = compoundUrl(hit)
  const id = !url ? (
    <span className="font-mono text-slate-200">{label}</span>
  ) : (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={`Open ${label} in the ${hit.source} catalog`}
      className={LINK}
    >
      {label}
      <ExternalLink aria-hidden="true" className="h-2.5 w-2.5 shrink-0" />
    </a>
  )
  if (!hit.smiles || smilesColumn) return id
  return (
    <span className="inline-flex flex-wrap items-baseline gap-x-2">
      {id}
      <span className="break-all font-mono text-slate-500">{hit.smiles}</span>
    </span>
  )
}
